import * as React from 'react';
import { ArchiveRestore } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';
import { EmptyState } from '@/components/ui/empty-state';
import {
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceNotice,
  useToast,
} from '@/components/ui/workspace';
import {
  newLifecycleIdempotencyKey,
  useDeletedEngagementsQuery,
  useMeQuery,
  useRestoreEngagementMutation,
  type DeletedEngagement,
} from '@/lib/api/engagement-lifecycle';
import { formatDateTime, titleCase } from '@/lib/utils/format';

/**
 * Firm settings → Deleted engagements (T-0161), firm admins only.
 *
 * Lists every deleted engagement with who deleted it and when, and restores
 * one. Restoring brings it back for its team everywhere it used to appear
 * and rebuilds its search index (every parsed document is indexed again),
 * which the confirmation says before anything happens.
 *
 * Non-admins never reach this from the navigation (the entry is hidden);
 * opening the URL directly shows a short notice and calls nothing.
 */

function errorMessage(err: unknown): string | null {
  if (!err) return null;
  return err instanceof Error && err.message
    ? err.message
    : 'The engagement could not be restored. Try again.';
}

export function DeletedEngagementsScreen() {
  const meQuery = useMeQuery();
  const isAdmin = meQuery.data?.can_delete_engagements === true;
  const listQuery = useDeletedEngagementsQuery({ enabled: isAdmin });
  const restore = useRestoreEngagementMutation();
  const { toast } = useToast();
  const [target, setTarget] = React.useState<DeletedEngagement | null>(null);
  const [idempotencyKey, setIdempotencyKey] = React.useState(newLifecycleIdempotencyKey);

  const header = (
    <header className="mb-5">
      <h1 className="t-h1 font-semibold text-ink-900">Deleted engagements</h1>
      <p className="t-meta text-ink-500">
        Engagements a firm admin deleted. They are hidden for everyone and keep all their documents
        and history. Restoring one brings it back for its team and rebuilds its search index.
      </p>
    </header>
  );

  if (meQuery.isLoading) {
    return (
      <div>
        {header}
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading…
        </div>
      </div>
    );
  }
  if (meQuery.error) {
    return (
      <div>
        {header}
        <ErrorMessage error={meQuery.error} />
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div>
        {header}
        <WorkspaceNotice>Only firm admins can see and restore deleted engagements.</WorkspaceNotice>
      </div>
    );
  }

  const openRestore = (row: DeletedEngagement) => {
    restore.reset();
    setIdempotencyKey(newLifecycleIdempotencyKey());
    setTarget(row);
  };

  const confirmRestore = () => {
    if (!target) return;
    const row = target;
    restore.mutate(
      { engagementId: row.engagement_id, idempotencyKey },
      {
        onSuccess: (result) => {
          setTarget(null);
          const queued = result.reindex?.documents_enqueued ?? 0;
          toast({
            message:
              queued > 0
                ? `${row.name} was restored. Its search index is being rebuilt from ${queued} document${queued === 1 ? '' : 's'}.`
                : `${row.name} was restored.`,
          });
        },
      },
    );
  };

  const columns: ReadonlyArray<DataGridColumn<DeletedEngagement>> = [
    {
      key: 'engagement',
      header: 'Engagement',
      cell: (row) => <span className="font-medium text-ink-900">{row.name}</span>,
    },
    { key: 'client', header: 'Client', cell: (row) => row.client_name },
    {
      key: 'deleted',
      header: 'Deleted',
      cell: (row) => <span className="whitespace-nowrap">{formatDateTime(row.deleted_at)}</span>,
    },
    {
      key: 'by',
      header: 'Deleted by',
      cell: (row) => (row.deleted_by ? row.deleted_by.name : 'Unknown'),
    },
    {
      // The status it keeps, and returns to on restore.
      key: 'status',
      header: 'Status',
      cell: (row) => titleCase(row.status),
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      cellClassName: 'text-right',
      cell: (row) => (
        <WorkspaceButton aria-label={`Restore ${row.name}`} onClick={() => openRestore(row)}>
          <ArchiveRestore className="h-3.5 w-3.5" aria-hidden="true" />
          Restore
        </WorkspaceButton>
      ),
    },
  ];

  return (
    <div>
      {header}
      {listQuery.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading deleted engagements…
        </div>
      ) : listQuery.error ? (
        <ErrorMessage error={listQuery.error} />
      ) : (
        <DataGrid
          caption="Deleted engagements"
          columns={columns}
          rows={listQuery.data?.engagements ?? []}
          rowKey={(row) => row.engagement_id}
          emptyState={
            <EmptyState
              title="No deleted engagements"
              description="When a firm admin deletes an engagement, it is listed here and can be restored."
            />
          }
        />
      )}

      <WorkspaceDialog
        open={target !== null}
        onOpenChange={(next) => {
          if (!next && !restore.isPending) setTarget(null);
        }}
        title={target ? `Restore ${target.name}?` : 'Restore engagement?'}
        description={target ? target.client_name : undefined}
        closeOnScrimClick={!restore.isPending}
        actions={
          <>
            <WorkspaceButton size="md" disabled={restore.isPending} onClick={() => setTarget(null)}>
              Cancel
            </WorkspaceButton>
            <WorkspaceButton
              size="md"
              variant="primary"
              disabled={restore.isPending}
              onClick={confirmRestore}
            >
              {restore.isPending ? 'Restoring…' : 'Restore engagement'}
            </WorkspaceButton>
          </>
        }
      >
        <ul className="list-disc pl-5 text-body-sm text-ink-700">
          <li>
            It comes back for its team: the portfolio, the sidebar, client pages and notifications
            show it again, and its pages open.
          </li>
          <li>
            Its search index is rebuilt: every parsed document is indexed again, which uses AI
            embedding spend and takes a few minutes for large engagements.
          </li>
          <li>Nothing else changes. Its documents and history were kept while it was deleted.</li>
        </ul>
        {restore.error ? (
          <WorkspaceNotice tone="critical">{errorMessage(restore.error)}</WorkspaceNotice>
        ) : null}
      </WorkspaceDialog>
    </div>
  );
}
